<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>

    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">⏻</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>

    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        />
      </div>

      <!-- 分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>

          <div class="pagination-controls">
            <button
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>

            <button
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>

            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>

            <button
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>

            <button
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>

          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>

          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input
              type="number"
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex'
import * as timeUtil from '@/utils/dateTimeUtil.js';

export default {
  name: 'MqttConnectionHistoryTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    columns() {
      return [
        {
          title: langMap.dashboard_dataTime[this.lang],
          key: 'eventTime',
          align: 'center',
          render: (h, { row }) => {
            return h('span', this.formatTime(row.eventTime))
          }
        },
        {
          title: langMap.dashboard_statusLabel[this.lang],
          key: 'status',
          align: 'center',
          render: (h, { row }) => {
            const isConnected = row.status === 0
            const isSubscribed = row.status === 1
            const isOnline = isConnected || isSubscribed
            let txt = '-'
            if (isConnected) {
              txt = langMap.dashboard_mqttConnected[this.lang]
            } else if (isSubscribed) {
              txt = langMap.dashboard_mqttSubscribed[this.lang]
            } else if (row.status === 2) {
              txt = langMap.dashboard_mqttOffline[this.lang]
            }
            return h('span', {
              class: [
                'status-badge',
                isConnected ? 'is-online' : (isSubscribed ? 'is-ready' : 'is-offline')
              ]
            }, [
              h('span', { class: 'status-dot-wrap' }, [
                h('span', { class: 'status-dot' }),
                h('span', { class: 'status-ring' })
              ]),
              h('span', { class: 'status-text' }, txt)
            ])
          }
        },
        {
          title: langMap.dashboard_offlineReason[this.lang],
          key: 'offlineReason',
          align: 'center',
          render: (h, { row }) => {
            const isOffline = row.status === 2
            if (!isOffline) {
              return h('span', '-')
            }
            const reason = this.getOfflineReason(row)
            return h('span', {
              class: ['offline-reason', reason ? '' : 'is-empty'],
              attrs: { title: reason || '-' }
            }, reason || '-')
          }
        },
        {
          title: langMap.dashboard_robotCode[this.lang],
          key: 'robotCode',
          align: 'center'
        }
      ]
    },
    totalPages() {
      return this.pagination.pages || Math.ceil(this.pagination.total / this.pagination.size) || 1
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total)
      return `${start}-${end}`
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.pagination.current
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    }
  },
  mounted() {
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    getOfflineReason(row) {
      if (!row) return ''
      return (
        row.offlineReason ||
        row.reason ||
        row.msg ||
        row.errorMsg ||
        row.errorDesc ||
        row.remark ||
        ''
      )
    },
    formatTime(t) {
      if (!t) {
        return '-'
      }
      return timeUtil.formatTime(t)
    },
    calculateTableHeight() {
      const windowHeight = window.innerHeight
      const headerHeight = 120
      const paginationHeight = 60
      const padding = 40
      this.tableHeight = windowHeight - headerHeight - paginationHeight - padding - 281
      if (this.tableHeight < 300) {
        this.tableHeight = 300
      }
    },
    handlePageChange(page) {
      this.$emit('page-change', page)
    },
    handlePageSizeChange(size) {
      this.$emit('size-change', size)
    },
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(
          langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages)
        )
        this.jumpPage = null
        return
      }
      this.handlePageChange(this.jumpPage)
      this.jumpPage = null
    }
  }
}
</script>

<style lang="less" scoped>
 /deep/ .status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  border: 1px solid transparent;
  transform: translateZ(0);
  min-width: 64px;
  box-sizing: border-box;
 }

 /deep/ .status-dot-wrap {
  position: relative;
  width: 10px;
  height: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 10px;
 }

 /deep/ .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  position: relative;
  animation: dot-breathe 1.2s ease-in-out infinite;
 }

 /deep/ .status-ring {
  position: absolute;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  opacity: 0;
  transform: scale(0.6);
  animation: pulse 1.4s ease-out infinite;
  pointer-events: none;
  will-change: transform, opacity;
 }

 /deep/ .status-text {
  line-height: 1;
 }

 /deep/ .is-online {
  color: #18a058;
  background: rgba(24, 160, 88, 0.10);
  border-color: rgba(24, 160, 88, 0.30);
 }

 /deep/ .is-online .status-dot {
  background: #18a058;
  box-shadow: 0 0 10px rgba(24, 160, 88, 0.55);
 }

 /deep/ .is-online .status-ring {
  background: rgba(24, 160, 88, 0.22);
 }

 /deep/ .is-ready {
  color: #00bcd4;
  background: rgba(0, 188, 212, 0.10);
  border-color: rgba(0, 188, 212, 0.32);
 }

 /deep/ .is-ready .status-dot {
  background: #00bcd4;
  box-shadow: 0 0 10px rgba(0, 188, 212, 0.55);
 }

 /deep/ .is-ready .status-ring {
  background: rgba(0, 188, 212, 0.22);
 }

 /deep/ .is-offline {
  color: #d03050;
  background: rgba(208, 48, 80, 0.10);
  border-color: rgba(208, 48, 80, 0.30);
 }

 /deep/ .is-offline .status-dot {
  background: #d03050;
  box-shadow: 0 0 10px rgba(208, 48, 80, 0.50);
 }

 /deep/ .is-offline .status-ring {
  background: rgba(208, 48, 80, 0.22);
 }

 @keyframes dot-breathe {
  0%, 100% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.25);
    filter: brightness(1.15);
  }
 }

 @keyframes pulse {
  0% {
    opacity: 0.0;
    transform: scale(0.6);
  }
  35% {
    opacity: 0.9;
  }
  100% {
    opacity: 0.0;
    transform: scale(1.3);
  }
 }

 /deep/ .offline-reason {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: rgba(255, 255, 255, 0.85);
 }

 /deep/ .offline-reason.is-empty {
  color: rgba(255, 255, 255, 0.45);
 }
</style>
